import React, { useRef, useState } from "react";
import api from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkles, Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const EQUIPMENT = ["Bodyweight", "Barbell", "Dumbbells", "Cable", "Machine", "EZ Bar", "Kettlebell", "Plate", "Pull Up Bar", "Medicine Ball", "Bands", "Other"];

const EMPTY = { name: "", muscle_groups: "", equipment: "Bodyweight", difficulty: "Beginner", instructions: "" };

export const CustomExerciseDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [photoMode, setPhotoMode] = useState("ai"); // "ai" | "upload"
  const [photo, setPhoto] = useState(null); // data URL
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const { dataUrl } = await compressImage(f, { maxWidth: 512, quality: 0.8 });
      setPhoto(dataUrl);
    } catch {
      toast.error("Couldn't read that image");
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Give it a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/exercises/custom", {
        name: form.name.trim(),
        muscle_groups: form.muscle_groups.split(",").map((m) => m.trim()).filter(Boolean),
        equipment: form.equipment,
        difficulty: form.difficulty,
        instructions: form.instructions.split("\n").map((l) => l.trim()).filter(Boolean),
        photo_data_url: photoMode === "upload" ? photo : null,
        ai_thumbnail: photoMode === "ai",
      });
      toast.success(photoMode === "ai" ? "Exercise created — AI thumbnail generating in background" : "Exercise created");
      setForm(EMPTY); setPhoto(null); setPhotoMode("ai");
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create exercise");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="custom-ex-title" className="font-display text-2xl uppercase">Custom Exercise</DialogTitle>
          <DialogDescription className="text-xs text-neutral-400">
            Private to you — it shows up in your library and workout builder. Leave muscles/steps blank and AI fills them in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Name *</Label>
            <Input data-testid="custom-ex-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Grizz Press" className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Muscle groups (comma separated)</Label>
            <Input data-testid="custom-ex-muscles" value={form.muscle_groups} onChange={(e) => setForm({ ...form, muscle_groups: e.target.value })} placeholder="Chest, Triceps — or leave blank for AI" className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11 text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Equipment</Label>
              <Select value={form.equipment} onValueChange={(v) => setForm({ ...form, equipment: v })}>
                <SelectTrigger data-testid="custom-ex-equipment" className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-800">
                  {EQUIPMENT.map((eq) => <SelectItem key={eq} value={eq}>{eq}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Difficulty</Label>
              <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                <SelectTrigger data-testid="custom-ex-difficulty" className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-800">
                  {["Beginner", "Intermediate", "Advanced"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Steps (one per line)</Label>
            <Textarea data-testid="custom-ex-instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={3} placeholder={"Leave blank and AI writes the form cues"} className="mt-1 bg-neutral-900 border-neutral-800 rounded-none text-white" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Picture</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <button
                type="button" data-testid="custom-ex-photo-ai"
                onClick={() => setPhotoMode("ai")}
                className={`h-11 flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider font-display border ${photoMode === "ai" ? "bg-blue-500 border-blue-500 text-white" : "border-neutral-700 text-neutral-400 hover:border-blue-500"}`}
              ><Sparkles className="w-3.5 h-3.5" /> AI Generated</button>
              <button
                type="button" data-testid="custom-ex-photo-upload"
                onClick={() => { setPhotoMode("upload"); if (!photo) fileRef.current?.click(); }}
                className={`h-11 flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider font-display border ${photoMode === "upload" ? "bg-blue-500 border-blue-500 text-white" : "border-neutral-700 text-neutral-400 hover:border-blue-500"}`}
              ><Upload className="w-3.5 h-3.5" /> Upload Photo</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="custom-ex-file-input" />
            {photoMode === "upload" && (
              <div className="mt-2 flex items-center gap-3">
                {photo ? (
                  <>
                    <img src={photo} alt="preview" data-testid="custom-ex-photo-preview" className="w-16 h-16 object-cover border border-neutral-800" />
                    <button type="button" onClick={() => setPhoto(null)} className="text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-wider flex items-center gap-1"><X className="w-3 h-3" /> Remove</button>
                  </>
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} className="text-blue-500 text-xs uppercase tracking-wider">Choose a photo…</button>
                )}
              </div>
            )}
          </div>

          <Button data-testid="custom-ex-submit" onClick={submit} disabled={busy} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create Exercise"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomExerciseDialog;
